import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Download, 
  Eye, 
  MousePointerClick, 
  Send, 
  Users,
  RefreshCw,
  BarChart3,
  TrendingUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin';

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
});

const AdminAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [summaryRes, chartRes] = await Promise.all([
        axios.get(`${API}/analytics/summary?days=${days}`, getAuthHeader()),
        axios.get(`${API}/analytics/chart-data?days=${days}`, getAuthHeader())
      ]);

      if (summaryRes.data.success) {
        setSummary(summaryRes.data.summary);
      }
      if (chartRes.data.success) {
        setChartData(chartRes.data.chart_data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/analytics/export?days=${days}`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export analytics');
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset all analytics data? This cannot be undone!')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/analytics/reset`, getAuthHeader());
      fetchAnalytics();
    } catch (error) {
      console.error('Reset failed:', error);
      alert('Failed to reset analytics');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-8 h-8 border-2 border-[#5a6f4e] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const maxViews = chartData.length > 0 ? Math.max(...chartData.map(d => d.views), 1) : 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#c5bfa9]">Website Analytics</h1>
          <p className="text-[#8b9a7e] mt-1">Track visitor engagement and page performance</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select 
            value={days} 
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 border border-[#3d4a35] rounded-lg bg-[#1f2937] text-[#c5bfa9] focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e]"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button 
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-[#4a5d42] hover:bg-[#5a6f4e] text-white rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button 
            onClick={fetchAnalytics}
            className="flex items-center gap-2 px-4 py-2 border border-[#3d4a35] bg-[#1f2937] text-[#c5bfa9] hover:bg-[#2d3748] rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button 
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 border border-red-700 bg-[#1f2937] text-red-400 hover:bg-red-900/30 rounded-lg transition-colors"
          >
            Reset
          </button>
        </div>
      </div>

      {summary && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-[#1f2937] border-[#3d4a35]">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-orange-500 rounded-lg">
                    <Eye className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-orange-400">Page Views</p>
                    <p className="text-2xl font-bold text-orange-300">{summary.total_page_views || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#1f2937] border-[#3d4a35]">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-emerald-500 rounded-lg">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-400">Unique Visitors</p>
                    <p className="text-2xl font-bold text-emerald-300">{summary.unique_visitors || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#1f2937] border-[#3d4a35]">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-500 rounded-lg">
                    <MousePointerClick className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-400">Button Clicks</p>
                    <p className="text-2xl font-bold text-blue-300">{summary.button_clicks || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#1f2937] border-[#3d4a35]">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-500 rounded-lg">
                    <Send className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-purple-400">Form Submissions</p>
                    <p className="text-2xl font-bold text-purple-300">{summary.form_submissions || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Page Views Chart */}
            <Card className="bg-[#1f2937] border-[#3d4a35]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#c5bfa9]">
                  <BarChart3 className="w-5 h-5 text-orange-500" />
                  Daily Page Views
                </CardTitle>
              </CardHeader>
              <CardContent>
                {chartData.length > 0 ? (
                  <div className="flex items-end justify-between gap-1 h-48 pt-4">
                    {chartData.map((data, index) => {
                      const height = (data.views / maxViews) * 160;
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center group">
                          <div className="relative w-full flex justify-center">
                            <span className="absolute -top-6 text-xs font-medium text-[#c5bfa9] opacity-0 group-hover:opacity-100 transition-opacity bg-[#111827] px-1 rounded shadow">
                              {data.views}
                            </span>
                            <div 
                              className="w-full max-w-[40px] bg-gradient-to-t from-orange-500 to-orange-400 rounded-t transition-all hover:from-orange-600 hover:to-orange-500"
                              style={{ height: `${Math.max(height, 4)}px` }}
                            />
                          </div>
                          <span className="text-xs text-[#8b9a7e] mt-2 truncate w-full text-center">
                            {new Date(data.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="h-48 flex items-center justify-center text-[#8b9a7e]">
                    No data available for this period
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Section Engagement */}
            <Card className="bg-[#1f2937] border-[#3d4a35]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#c5bfa9]">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  Section Engagement
                </CardTitle>
              </CardHeader>
              <CardContent>
                {summary.section_engagement && summary.section_engagement.length > 0 ? (
                  <div className="space-y-3">
                    {summary.section_engagement.map((section, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between p-3 bg-[#111827] rounded-lg hover:bg-[#1a1f2e] transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                            index === 0 ? 'bg-orange-500' : 
                            index === 1 ? 'bg-emerald-500' : 
                            index === 2 ? 'bg-blue-500' : 'bg-gray-500'
                          }`}>
                            {index + 1}
                          </div>
                          <span className="font-medium text-[#c5bfa9] capitalize">{section.section}</span>
                        </div>
                        <span className="px-3 py-1 bg-orange-900/30 text-orange-400 rounded-full text-sm font-medium">
                          {section.avg_time}s avg
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-32 flex items-center justify-center text-[#8b9a7e]">
                    No section data yet. Visit the website to start tracking.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminAnalytics;
